import { useState, useRef, useEffect, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SendHorizontalIcon,
  MapPinIcon,
  ClipboardListIcon,
  BookTextIcon,
  AlertTriangleIcon,
  XIcon,
  MicIcon,
  ExternalLinkIcon,
  FileTextIcon,
  ImageIcon,
  FileIcon,
  CopyIcon,
  CheckIcon,
  StarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  HistoryIcon,
  PlusIcon,
  type LucideIcon,
} from 'lucide-react';
import { Layout } from '../components/Layout.js';
import { ChatHistoryRail } from '../components/ChatHistoryRail.js';
import { WelcomePanel } from '../components/WelcomePanel.js';
import { RingChip, RING2_SOURCE_LABELS } from '../components/RingChip.js';
import { KennisagentMobile, useIsMobile } from '../components/KennisagentMobile.js';
import {
  ExampleQuestions,
  KennisagentComposer,
  MilaGreeting,
  type AskMode,
} from '../components/KennisagentComposer.js';
import { apiGet, apiPost, ApiError } from '../api/client.js';
import { maakVerwijsbriefUitSara } from '../lib/verwijsbrief.js';
import type { Provider } from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';
import type {
  DemoChatFollowUps,
  DemoQaFollowUp,
  DemoQaItem as SharedDemoQaItem,
} from '@kennisbank/shared';
import {
  buildPayload,
  type WijzigingsverzoekContext,
} from '../components/WijzigingsverzoekButton.js';
import { citationCountLabel, groupCitations, type CitationGroup } from '../lib/citations.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

const IMAGE_EXTENSIONS = /\.(jpe?g|jfif|png|webp|gif)$/i;
const TEXT_DOC_EXTENSIONS = /\.(pdf|odt|docx)$/i;

function pickSourceIcon(filename: string): LucideIcon {
  if (IMAGE_EXTENSIONS.test(filename)) return ImageIcon;
  if (TEXT_DOC_EXTENSIONS.test(filename)) return FileTextIcon;
  return FileIcon;
}

/**
 * Slice 3a Kennisagent chat page.
 *
 * Multi-turn conversation surface — each ask appends a new question/answer
 * pair below the previous ones, so the operator can keep drilling on the
 * same topic. Per turn: a single answer card holds the prose, the source
 * citations (compact rows) and a feedback footer in one visual container.
 *
 * Error UX maps backend status codes to Dutch copy per the slice-3a plan.
 */

interface AgentCitation {
  chunkId: string;
  title: string;
  sourceFile?: string;
  author?: string;
  page: number;
  snippet: string;
  /** Trust-tier chip on the citation card. */
  ring?: 'ring0' | 'ring1' | 'ring2' | 'ring3' | 'ring_hq';
  /** Ring 2 only: canonical source id (nhg / trimbos / thuisarts / …). */
  ring2Source?: string;
  /** Ring 3 only: canonical source id (meesters / …). */
  ring3Source?: string;
  /** Ring 2 or Ring 3: original article URL for the "Open op …" button. */
  sourceUrl?: string;
}

/** Slice 8 — Sociale Kaart provider cited by the model via [providerId: <id>].
 *  Mirrors AgentProviderCitation in shared/src/schemas/agent.ts.
 */
interface AgentProviderCitation {
  providerId: string;
  name: string;
  city?: string | null;
  province?: string | null;
  type?: string | null;
  specialisations?: string[];
  audiences?: string[];
  website?: string | null;
  phone?: string | null;
}

interface AskResponse {
  requestId: string;
  /** Slice 3b chat-history wedge — present on every successful response. */
  chatId: string;
  answer: string;
  citations: AgentCitation[];
  /** Slice 8 — Sociale Kaart providers cited via find_zorgaanbieders. */
  providerCitations?: AgentProviderCitation[];
  outcome:
    | 'success'
    | 'low_confidence_rewrite'
    | 'empty_retrieval'
    | 'refused'
    | 'crisis'
    // Issue #22 — fuzzy naam-match vroeg om bevestiging. Rendert als een
    // gewoon antwoordbericht (default pad, geen aparte UI-behandeling).
    | 'bron_confirmation';
}

interface AskStreamHandlers {
  /** Called for each token chunk the agent emits while generating.
   * `partial` is the cumulative answer text so far — render it directly,
   * no append needed. */
  onDelta(partial: string): void;
}

/**
 * Slice 7 — streaming-aware /ask client. Sends Accept for both content
 * types so the backend can pick (env-flag-controlled). Two response
 * shapes:
 *   - `text/event-stream` → consume the stream, fire `onDelta` for each
 *     chunk, resolve with the final AskResponse on `done`.
 *   - `application/json` → parse, fire one `onDelta(answer)` for state-
 *     machine consistency, resolve with the parsed body.
 *
 * Throws ApiError on non-2xx (parsed from the error envelope). The
 * `signal` param wires the existing AbortController so the user-side
 * "Annuleren" button kills both shapes identically.
 */
// Een richting (Verdiepen/Werkvormen/Verwijzen/Plan) wordt in het invoerveld
// als chip geladen en gaat mee met de eerstvolgende verzending; daarna valt
// de chip weg. Alleen een Sara-overdracht houdt de richting sticky over
// vragen heen (stickyMode), zodat "maak korter" het plan verfijnt.
//
// Met een leeg veld gaat de openingsvraag van de richting mee (Jos
// 2026-07-16): de agent vraagt dan zelf terug wat hij mist (regio bij
// verwijzen, onderwerp bij de rest) in plaats van te gokken.
const MODE_OPENING_QUERY: Record<Exclude<AskMode, 'free'>, string> = {
  verdiepen: 'Geef klinische verdieping bij dit onderwerp.',
  werkvormen: 'Welke werkvormen of oefeningen kan ik hierbij inzetten?',
  verwijzen: 'Zoek passende zorgaanbieders in de Sociale Kaart om naar door te verwijzen.',
  plan: 'Geef een algemene plan-opzet. Bij welk klachtenbeeld?',
};

// SOEP-structuur zoals Sara 'm meestuurt bij de overdracht (router-state).
type Soep = { S: string; O: string; E: string; P: string };

// Verwijder de ruwe [chunkId: <hash>]-citatiemarkers uit Mila's antwoord: die
// zijn onleesbaar en horen niet in het HIS. Inclusief de spatie ervoor, zodat
// "voorkomen [chunkId: …]." netjes "voorkomen." wordt.
function stripCitations(text: string): string {
  return text.replace(/\s*\[chunkId:\s*[^\]]+\]/g, '');
}

// Het plan-antwoord sluit af met een disclaimer-zin ("…geen diagnose", zie de
// plan-prompt in agent.py). Die blijft in Mila zichtbaar, maar mag niet in de
// HIS-tekst. Strip de laatste alinea als die de voorgeschreven marker bevat.
// Heuristiek (model-gegenereerd), bewust conservatief: strippen alleen bij een
// duidelijke match, anders blijft de alinea liever staan dan echte inhoud weg.
function stripDisclaimer(text: string): string {
  const paras = text.trimEnd().split(/\n\s*\n/);
  const last = paras[paras.length - 1] ?? '';
  // De agent sluit af met een disclaimer, maar de wording varieert ("…geen
  // diagnose", "…dient u zelf…aan te passen"). Strip de laatste alinea bij een
  // van die vaste markers. Conservatief: alleen de laatste alinea.
  if (paras.length > 1 && /geen diagnose|dient u zelf|behandelplanvoorstel/i.test(last)) {
    paras.pop();
  }
  return paras.join('\n\n').trimEnd();
}

async function askStreaming(
  payload: {
    query: string;
    documentTitles?: string[];
    chatId?: string;
    mode?: AskMode;
    naamBevestigd?: boolean;
  },
  handlers: AskStreamHandlers,
  signal?: AbortSignal,
): Promise<AskResponse> {
  const res = await fetch('/api/v1/agent/ask', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      // Both shapes — backend picks based on env flag. When streaming is
      // off, backend ignores the SSE part and returns JSON; we still
      // surface a single delta so the typing indicator hand-off is uniform.
      accept: 'text/event-stream, application/json',
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
    };
    throw new ApiError(res.status, err.code ?? 'unknown', err.message ?? '');
  }

  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('text/event-stream')) {
    // Legacy JSON fallback — fire one delta so the UI still gets a
    // single render-tick before the final swap.
    const body = (await res.json()) as AskResponse;
    handlers.onDelta(body.answer);
    return body;
  }

  // chatId comes via custom header on streaming responses (SSE payload
  // doesn't carry it — it's the backend's domain). Set BEFORE we start
  // consuming the stream so it's available even if the stream truncates.
  const chatIdHeader = res.headers.get('x-chat-id') ?? '';

  // SSE — read the body as a stream. Buffer across chunk boundaries
  // (a single SSE event may span multiple read() chunks at high rates).
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let cumulativeText = '';
  let final: AskResponse | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIdx: number;
    while ((nlIdx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 2);
      for (const line of block.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice('data: '.length));
        if (payload.type === 'delta') {
          cumulativeText += payload.text;
          handlers.onDelta(cumulativeText);
        } else if (payload.type === 'done') {
          final = {
            requestId: payload.requestId,
            chatId: chatIdHeader,
            answer: payload.answer,
            citations: payload.citations,
            providerCitations: payload.providerCitations ?? [],
            outcome: payload.outcome,
          };
        } else if (payload.type === 'error') {
          // Geef al gestreamde tekst mee zodat de UI het deels ontvangen
          // antwoord kan laten staan in plaats van het te laten verdwijnen.
          throw new ApiError(
            500,
            payload.code ?? 'internal_error',
            payload.message ?? '',
            cumulativeText ? { partialAnswer: cumulativeText } : undefined,
          );
        }
      }
    }
  }

  if (!final) {
    throw new ApiError(500, 'stream_truncated', 'Antwoord is onderweg afgebroken.');
  }
  return final;
}

interface ChatThreadResponse {
  chat: { chatId: string; title: string };
  messages: Array<{
    messageId: string;
    role: 'user' | 'assistant';
    content: string;
    sources: AgentCitation[];
    createdAt: string;
  }>;
}

const ERROR_COPY: Record<string, string> = {
  agent_unavailable:
    'Kennisagent is tijdelijk niet beschikbaar. Probeer het over een minuut opnieuw.',
  agent_timeout: 'Antwoord duurt langer dan verwacht. Probeer een specifiekere vraag.',
  agent_busy: 'Het is op dit moment druk op het AI-platform. Probeer het over een minuut opnieuw.',
  rate_limited: 'Je hebt te veel vragen achter elkaar gesteld. Wacht een minuut.',
  internal_error: 'Er ging iets mis bij het genereren van het antwoord. Probeer het opnieuw.',
  forbidden: 'Deze functie is alleen beschikbaar voor zorgverleners en SUPPOHRT-medewerkers.',
};

const FALLBACK_ERROR = 'Er ging iets mis. Probeer het opnieuw.';

const EMPTY_RETRIEVAL_HINT = 'Stel je vraag specifieker, of verifieer in een primaire bron.';

/**
 * The agent emits inline citation markers as `[chunkId: <sha256>, <sha256>]`
 * in the answer prose. The raw hashes are unreadable; rewrite them as
 * `[1]`, `[2]` numbers matching each chunk's position in the citations
 * array. Unknown chunkIds drop out of the bracket entirely; if the bracket
 * ends up empty the whole tag is removed.
 */
function renderAnswerProse(text: string, citations: AgentCitation[]): JSX.Element[] {
  const indexByChunkId = new Map<string, number>();
  citations.forEach((c, i) => indexByChunkId.set(c.chunkId, i + 1));

  const pattern = /\[chunkId:\s*([^\]]+)\]/g;
  const out: (string | JSX.Element)[] = [];
  let cursor = 0;
  let matchIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > cursor) out.push(text.slice(cursor, m.index));
    const numbers = m[1]
      .split(',')
      .map((s) => indexByChunkId.get(s.trim()))
      .filter((n): n is number => typeof n === 'number');
    if (numbers.length > 0) {
      out.push(
        <sup
          key={`cite-${matchIdx++}`}
          className="ml-0.5 inline-flex items-center gap-0.5 align-baseline text-[10px] font-medium text-brand-700"
        >
          {numbers.map((n, i) => (
            <span key={i}>
              [{n}]{i < numbers.length - 1 ? '' : ''}
            </span>
          ))}
        </sup>,
      );
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out.map((node, i) =>
    typeof node === 'string' ? <span key={`t-${i}`}>{renderBold(node)}</span> : node,
  );
}

/**
 * Minimale markdown-subset: alleen `**vet**`. De werkvormen-modus vraagt het
 * model expliciet om genummerde stappen; nieuwe regels rendert `pre-wrap` al,
 * maar zonder deze stap staat er letterlijk ** rond koppen in de UI.
 */
function renderBold(text: string): (string | JSX.Element)[] {
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  if (parts.length === 1) return [text];
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={`b-${i}`}>{p}</strong> : p));
}

interface ChatState {
  question: string;
  answer: AskResponse;
  /** Snapshot of the scope this turn was answered against. */
  scopeTitles: string[] | null;
}

interface DocumentSummary {
  sourceFile: string;
  documentTitle: string;
  author: string;
}

interface DocumentsResponse {
  documents: DocumentSummary[];
}

/**
 * Vast demo-antwoord op de Sara-overdracht (issue #43): de doorzetknop moet in
 * de demo blijven werken, maar POST /agent/ask bestaat daar niet (read-only
 * guard). Eén gescript advies dekt alle intenties; het hoort inhoudelijk bij
 * het vaste voorbeeldconsult (werkgerelateerde spanningsklachten) uit Sara.
 */
const DEMO_SARA_HANDOFF_ANSWER: AskResponse = {
  requestId: 'demo-sara-handoff',
  chatId: '',
  answer:
    'Dit is een voorbeeldantwoord uit de demo-omgeving. In de echte omgeving analyseert Mila het doorgezette consult en zoekt zij live in de kennisbronnen.\n\nBij dit voorbeeldconsult (werkgerelateerde spanningsklachten met doorslaapproblemen) zou het antwoord er zo uit kunnen zien:\n\n1. **Piekerkwartier voortzetten.** De eerdere toepassing werkte; herstel het vaste dagelijkse moment en houd piekergedachten buiten de nacht.\n2. **Gesprek met de bedrijfsarts voorbereiden.** Bespreek de herstructurering en maak afspraken over een passende, stapsgewijze werkhervatting.\n3. **Slaap ondersteunen.** Psycho-educatie over doorslaapproblemen bij spanning; vaste bedtijden en geen werkmail in de avond.\n4. **Terugvalpreventie.** Benoem de vroege signalen uit dit consult (doorslaapproblemen, ziekmelding als falen ervaren) en wat de patiënt dan doet.',
  citations: [],
  providerCitations: [],
  outcome: 'success',
};

export function KennisagentPage(): JSX.Element {
  const brand = useTenantBrand();
  const location = useLocation();
  const navigate = useNavigate();
  // Demo-modus (issue #41): 10 klikbare voorbeeldvragen vervangen het vrije
  // invoerveld; een klik zet het vooraf goedgekeurde antwoord als gewone beurt
  // in de bestaande antwoord-UI. Geen enkele agent- of LLM-aanroep.
  const { user } = useAuth();
  const demo = user?.demo === true;
  const [demoQa, setDemoQa] = useState<SharedDemoQaItem[] | null>(null);
  // Doorvragen (demo): de laatst gekozen hoofdvraag bepaalt welke gescripte
  // vervolgvragen onder het antwoord verschijnen; gestelde vervolgvragen
  // verdwijnen uit het rijtje, zoals een echt gesprek vordert. Voor een
  // geopend historisch gesprek (rail) komen de doorvragen uit chatFollowUps.
  const [demoActiveQa, setDemoActiveQa] = useState<SharedDemoQaItem | null>(null);
  const [demoChatQa, setDemoChatQa] = useState<DemoChatFollowUps[]>([]);
  const [demoAskedFollowUps, setDemoAskedFollowUps] = useState<string[]>([]);
  useEffect(() => {
    if (!demo) return;
    let cancelled = false;
    apiGet<{ items: SharedDemoQaItem[]; chatFollowUps?: DemoChatFollowUps[] }>('/api/v1/demo/qa')
      .then((r) => {
        if (cancelled) return;
        setDemoQa(r.items);
        setDemoChatQa(r.chatFollowUps ?? []);
      })
      .catch(() => {
        if (!cancelled) setDemoQa([]);
      });
    return () => {
      cancelled = true;
    };
  }, [demo]);
  // Pending auto-send uit de Sara-overdracht (gezet op mount, afgevuurd zodra
  // submit beschikbaar is). Ref i.p.v. state: mag geen re-render triggeren.
  const pendingSaraSendRef = useRef<{ query: string; mode: AskMode } | null>(null);
  const [draft, setDraft] = useState('');
  const [saraContext, setSaraContext] = useState<{ mode: string } | null>(null);
  // Sara-overdracht: structured SOEP + summary + taskId, nodig voor de
  // terugweg ("Terug naar Sara"), zie onBackToSara.
  const [saraSoep, setSaraSoep] = useState<Soep | null>(null);
  const [saraSummary, setSaraSummary] = useState<string>('');
  const [saraTaskId, setSaraTaskId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatState[]>([]);
  // Mila-verwijzen → concept-verwijsbrief (ADR 0017). Inhoud uit het
  // Sara-transcript als de vraag uit Sara kwam; anders leeg, want de chat zelf
  // is geen consult.
  const maakBriefVoor = async (p: AgentProviderCitation): Promise<void> => {
    const provider = await apiGet<Provider>(`/api/v1/providers/${p.providerId}`).catch(() => null);
    const adres = provider
      ? [provider.location.address, provider.location.city].filter(Boolean).join(', ')
      : (p.city ?? '');
    const b = await maakVerwijsbriefUitSara(
      {
        bron: 'mila',
        geadresseerde: { providerId: p.providerId, naam: p.name, adres },
        saraTaskId,
      },
      user?.displayName ?? '',
    );
    navigate(`/verwijsbrieven/${b.id}`);
  };
  const [pending, setPending] = useState(false);
  // Slice 7 streaming — accumulates SSE deltas for the in-flight ask.
  // Empty string means "request hasn't received any tokens yet" (still
  // showing the typing indicator); non-empty means "render this as the
  // tentative answer prose." Cleared once the final response lands and
  // turns is updated.
  const [streamingText, setStreamingText] = useState('');
  const [errorCopy, setErrorCopy] = useState<string | null>(null);
  // PII trap 2 (issue #31): de backend vermoedt een persoonsnaam in de
  // vraag. Geen harde fout — de gebruiker past de vraag aan óf bevestigt
  // "geen persoonsnaam", waarna dezelfde vraag met naamBevestigd doorgaat.
  const [naamBevestiging, setNaamBevestiging] = useState<{
    message: string;
    mode: AskMode;
  } | null>(null);
  const [showAbort, setShowAbort] = useState(false);
  // Scope picker state. `null` means "all docs" (default). Once the
  // operator opens the picker and saves a selection, this becomes a
  // list of documentTitles. Persists across turns until cleared.
  const [scopeTitles, setScopeTitles] = useState<string[] | null>(null);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  // Slice 3b chat-history wedge state.
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  // The user's in-flight question — stays visible above the typing indicator
  // while the agent is still answering. Survives nav-away because we hydrate
  // it from the persisted chat history on mount (see useEffect below).
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const abortTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  const isMobile = useIsMobile();
  const naamBevestigingRef = useRef<HTMLDivElement | null>(null);

  // ─── chatId persistence + nav-away recovery ────────────────────────────
  // Key the storage by user so a different login on the same browser
  // doesn't see the previous user's chat. Falls back to a generic key
  // when no auth context is around (dev / first paint).
  const CHAT_ID_STORAGE_KEY = 'kennisagent.activeChatId';
  useEffect(() => {
    if (currentChatId) {
      sessionStorage.setItem(CHAT_ID_STORAGE_KEY, currentChatId);
    } else {
      sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
    }
  }, [currentChatId]);

  // Overdracht vanuit Sara: de POH koos daar al de intentie, dus de (in Sara
  // gepseudonimiseerde, ADR 0006) SOEP-vraag gaat hier direct — geen tweede
  // klik, geen groot voorgevuld vak (besluit Jos 2026-07-23, vervangt de
  // eerdere "POH reviewt eerst in Mila"-flow). De echte send gebeurt in het
  // effect hieronder, zodra submit gedefinieerd is. Runt eenmalig op mount;
  // wist router-state zodat een back-nav niet opnieuw vult.
  useEffect(() => {
    const state = location.state as {
      saraPlan?: string;
      saraMode?: string;
      saraSoep?: Soep;
      saraSummary?: string;
      saraTaskId?: string | null;
      draft?: string;
    } | null;
    if (state?.saraPlan) {
      pendingSaraSendRef.current = {
        query: state.saraPlan,
        mode: (state.saraMode as AskMode) ?? 'plan',
      };
      setSaraContext({ mode: state.saraMode ?? 'verdiepen' });
      if (state.saraSoep) setSaraSoep(state.saraSoep);
      if (state.saraSummary) setSaraSummary(state.saraSummary);
      setSaraTaskId(state.saraTaskId ?? null);
      window.history.replaceState({}, '');
    } else if (state?.draft) {
      // Vanuit het documentvoorbeeld: alleen voorvullen, de POH maakt de vraag zelf af.
      // Nieuw gesprek: anders hangt de vraag aan de vorige chat en zijn context.
      sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
      setDraft(state.draft);
      window.history.replaceState({}, '');
    }
    // Mount-only; deps intentionally empty.
  }, []);

  // Auto-scroll the latest turn into view whenever a new answer arrives or
  // we transition into the pending/error state. `behavior: smooth` keeps
  // the long-running case (cold-start agent) feeling responsive.
  // Bij een naam-waarschuwing scrollen we naar het blok zelf: dat staat onder
  // het laatste antwoord en zou anders net buiten beeld kunnen vallen, precies
  // het probleem dat de knoppen onvindbaar maakte.
  useEffect(() => {
    const target = naamBevestiging ? naamBevestigingRef.current : bottomRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, pending, errorCopy, naamBevestiging]);

  const submit = async (
    mode: AskMode = 'free',
    opts?: { naamBevestigd?: boolean; queryOverride?: string },
  ): Promise<void> => {
    // An intent button falls back to its own opening question; the plain
    // submit (mode='free') still needs typed text and stays disabled without it.
    // queryOverride: de Sara-overdracht stuurt de SOEP-vraag direct (auto-send),
    // zonder dat 'ie eerst in het invoervak belandt.
    const question =
      opts?.queryOverride?.trim() ||
      draft.trim() ||
      (mode === 'free' ? '' : MODE_OPENING_QUERY[mode]);
    if (!question || pending) return;
    setErrorCopy(null);
    setNaamBevestiging(null);
    setPending(true);
    setPendingQuestion(question);
    // Veld direct leegmaken — de vraag staat nu als pending-bubbel in de
    // thread; bij een fout zetten we de tekst terug (catch), o.a. omdat de
    // naam-bevestigingsflow de draft opnieuw leest bij resubmit.
    setDraft('');
    setShowAbort(false);

    abortRef.current = new AbortController();
    abortTimerRef.current = setTimeout(() => setShowAbort(true), 5000);

    try {
      const askPayload: {
        query: string;
        documentTitles?: string[];
        chatId?: string;
        mode?: AskMode;
        naamBevestigd?: boolean;
      } = {
        query: question,
      };
      if (opts?.naamBevestigd) {
        askPayload.naamBevestigd = true;
      }
      if (scopeTitles && scopeTitles.length > 0) {
        askPayload.documentTitles = scopeTitles;
      }
      if (currentChatId) {
        askPayload.chatId = currentChatId;
      }
      if (mode !== 'free') {
        askPayload.mode = mode;
      }
      // Slice 7 — streaming-aware ask. Server picks JSON vs SSE based on
      // its env flag; client always sends both Accept types so we work
      // identically against either response shape.
      const response = await askStreaming(
        askPayload,
        { onDelta: (partial) => setStreamingText(partial) },
        abortRef.current?.signal,
      );
      setTurns((prev) => [
        ...prev,
        { question, answer: response, scopeTitles: scopeTitles ?? null },
      ]);
      setCurrentChatId(response.chatId);
      // Bump the rail; ~2s delay lets the background title call land before
      // the rail refetch picks up the final title (eng-review 1B path).
      setHistoryRefreshKey((k) => k + 1);
      window.setTimeout(() => setHistoryRefreshKey((k) => k + 1), 2500);
    } catch (err) {
      // Stream brak halverwege? Laat het al ontvangen deel als beurt staan
      // in plaats van een antwoord voor de ogen van de gebruiker te wissen.
      const partial =
        err instanceof ApiError
          ? ((err.detail as { partialAnswer?: string } | undefined)?.partialAnswer ?? null)
          : null;
      if (partial) {
        setTurns((prev) => [
          ...prev,
          {
            question,
            answer: {
              requestId: '',
              chatId: currentChatId ?? '',
              answer: partial,
              citations: [],
              providerCitations: [],
              outcome: 'success',
            },
            scopeTitles: scopeTitles ?? null,
          },
        ]);
      } else {
        // Niets ontvangen — zet de vraag terug in het veld zodat de
        // gebruiker (of de naam-bevestigingsflow) opnieuw kan versturen.
        setDraft(question);
      }
      if (err instanceof ApiError && err.code === 'naam_bevestiging_nodig') {
        setNaamBevestiging({ message: err.message, mode });
      } else if (err instanceof ApiError) {
        const copy = ERROR_COPY[err.code] ?? err.message ?? FALLBACK_ERROR;
        setErrorCopy(partial ? `Het antwoord is onderweg afgebroken. ${copy}` : copy);
      } else if ((err as Error).name === 'AbortError') {
        setErrorCopy('Verzoek geannuleerd.');
      } else {
        setErrorCopy(FALLBACK_ERROR);
      }
    } finally {
      setPending(false);
      setPendingQuestion(null);
      setStreamingText('');
      setShowAbort(false);
      if (abortTimerRef.current) clearTimeout(abortTimerRef.current);
      abortTimerRef.current = null;
      abortRef.current = null;
    }
  };

  // Vuurt de Sara-auto-send af zodra de component gemonteerd is (submit bestaat
  // dan). Ref wordt gewist vóór de send, dus StrictMode-dubbelmount stuurt niet
  // dubbel.
  useEffect(() => {
    const p = pendingSaraSendRef.current;
    if (p) {
      pendingSaraSendRef.current = null;
      if (demo) {
        // Demo: geen POST /agent/ask (read-only guard zou 403 geven) — de
        // doorzetflow toont een vast voorbeeldadvies (issue #43).
        setTurns((prev) => [
          ...prev,
          { question: p.query, answer: DEMO_SARA_HANDOFF_ANSWER, scopeTitles: null },
        ]);
      } else {
        void submit(p.mode, { queryOverride: p.query });
      }
    }
    // Mount-only; deps intentionally empty.
  }, []);

  const cancel = (): void => {
    abortRef.current?.abort();
  };

  // Klik op een voorbeeldvraag (demo): het klaargezette antwoord wordt een
  // gewone beurt in de thread — zelfde render-pad als een live antwoord.
  const askDemo = (item: SharedDemoQaItem): void => {
    setDemoActiveQa(item);
    setTurns((prev) => [
      ...prev,
      {
        question: item.question,
        answer: {
          requestId: item.id,
          chatId: '',
          answer: item.answer,
          citations: item.citations,
          providerCitations: item.providerCitations,
          outcome: item.outcome,
        },
        scopeTitles: null,
      },
    ]);
  };

  // Gescripte vervolgvraag (doorvragen) bij de actieve hoofdvraag.
  const askDemoFollowUp = (fu: DemoQaFollowUp): void => {
    setDemoAskedFollowUps((prev) => [...prev, fu.id]);
    setTurns((prev) => [
      ...prev,
      {
        question: fu.question,
        answer: {
          requestId: fu.id,
          chatId: '',
          answer: fu.answer,
          citations: fu.citations,
          providerCitations: fu.providerCitations,
          outcome: fu.outcome,
        },
        scopeTitles: null,
      },
    ]);
  };

  // Slice 3b chat-history wedge: reset to a fresh blank chat. Matches the
  // /office-hours D3A decision (preserve slice-3a behavior; returning user
  // explicitly picks a chat from the rail to resume).
  const onNewChat = (): void => {
    stopPolling();
    setPendingQuestion(null);
    setPending(false);
    setCurrentChatId(null);
    setTurns([]);
    setDraft('');
    setDemoActiveQa(null);
    setDemoAskedFollowUps([]);
    setErrorCopy(null);
    setNaamBevestiging(null);
    setScopeTitles(null);
    // Sara-overdracht hoort bij dít consult; een nieuw gesprek begint schoon.
    setSaraContext(null);
    setSaraSoep(null);
    setSaraSummary('');
    setSaraTaskId(null);
  };

  /**
   * Hydrate the page from a persisted chat. Shared by the rail-click path
   * (`onPickChat`) and the on-mount nav-away recovery path.
   *
   * If the last persisted message is a user message without an assistant
   * follow-up, that question is set as the pending question and a poll
   * loop watches for the assistant reply that the backend is still writing.
   */
  const hydrateFromChat = async (
    chatId: string,
    opts: { resumePolling: boolean },
  ): Promise<void> => {
    setErrorCopy(null);
    const thread = await apiGet<ChatThreadResponse>(`/api/v1/chats/${chatId}`);
    setCurrentChatId(thread.chat.chatId);
    setDraft('');
    const pairs: ChatState[] = [];
    let i = 0;
    while (i < thread.messages.length) {
      const userMsg = thread.messages[i];
      const assistantMsg = thread.messages[i + 1];
      if (userMsg?.role !== 'user') break;
      if (assistantMsg?.role === 'assistant') {
        pairs.push({
          question: userMsg.content,
          answer: {
            requestId: assistantMsg.messageId,
            chatId: thread.chat.chatId,
            answer: assistantMsg.content,
            citations: assistantMsg.sources,
            outcome: 'success',
          },
          scopeTitles: null,
        });
        i += 2;
      } else {
        // Trailing user message with no assistant reply yet — the agent
        // is still working server-side (or the previous tab navigated
        // away mid-call). Surface as pending and poll for the answer.
        if (opts.resumePolling) {
          setPendingQuestion(userMsg.content);
          setPending(true);
          schedulePoll(thread.chat.chatId, pairs.length);
        }
        break;
      }
    }
    setTurns(pairs);
  };

  const onPickChat = async (chatId: string): Promise<void> => {
    stopPolling();
    setPending(false);
    setPendingQuestion(null);
    // Demo: een historisch gesprek heeft zijn eigen verloop; de doorvraag-
    // knoppen van de laatst geklikte voorbeeldvraag horen daar niet onder.
    setDemoActiveQa(null);
    setDemoAskedFollowUps([]);
    // Rail-klik naar een ander gesprek: de live Sara-context hoort daar niet
    // bij. (Bewust hier en niet in hydrateFromChat: dat draait ook op de
    // mount-recovery, die niet met een verse Sara-overdracht mag botsen.)
    setSaraContext(null);
    setSaraSoep(null);
    setSaraSummary('');
    setSaraTaskId(null);
    try {
      await hydrateFromChat(chatId, { resumePolling: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setErrorCopy('Dit gesprek bestaat niet meer.');
      } else {
        setErrorCopy(FALLBACK_ERROR);
      }
    }
  };

  function stopPolling(): void {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  /**
   * Poll chat-history every 3s for an assistant message at index
   * `expectedPairIndex * 2 + 1`. Stop when found, on error, or after
   * ~5 minutes. Only one poll loop runs at a time (stopPolling clears
   * any prior timer).
   */
  function schedulePoll(chatId: string, expectedPairIndex: number, attempt = 0): void {
    stopPolling();
    if (attempt > 100) {
      // ~5 min budget. Surface a soft error; user can refresh to retry.
      setPending(false);
      setPendingQuestion(null);
      setErrorCopy('Het antwoord duurde te lang. Vernieuw de pagina om het opnieuw te proberen.');
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      void (async () => {
        try {
          const thread = await apiGet<ChatThreadResponse>(`/api/v1/chats/${chatId}`);
          const targetIdx = expectedPairIndex * 2 + 1;
          const assistant = thread.messages[targetIdx];
          if (assistant?.role === 'assistant') {
            const userMsg = thread.messages[targetIdx - 1];
            if (userMsg?.role === 'user') {
              setTurns((prev) => [
                ...prev,
                {
                  question: userMsg.content,
                  answer: {
                    requestId: assistant.messageId,
                    chatId,
                    answer: assistant.content,
                    citations: assistant.sources,
                    outcome: 'success',
                  },
                  scopeTitles: null,
                },
              ]);
            }
            setPending(false);
            setPendingQuestion(null);
            setHistoryRefreshKey((k) => k + 1);
            return;
          }
          schedulePoll(chatId, expectedPairIndex, attempt + 1);
        } catch {
          // Transient — keep polling.
          schedulePoll(chatId, expectedPairIndex, attempt + 1);
        }
      })();
    }, 3000);
  }

  // On mount: if sessionStorage has an active chatId, restore the chat.
  // Resumes any in-flight poll for a trailing user-message without an
  // assistant follow-up. Empty deps — runs once per mount.
  useEffect(() => {
    const stored = sessionStorage.getItem(CHAT_ID_STORAGE_KEY);
    if (!stored) return;
    void hydrateFromChat(stored, { resumePolling: true }).catch((err) => {
      if (err instanceof ApiError && err.status === 404) {
        // Stale storage — chat was deleted on another tab.
        sessionStorage.removeItem(CHAT_ID_STORAGE_KEY);
      }
    });
    return () => stopPolling();
  }, []);

  // Beurten + wachtstaat + fouten: gedeeld door de desktop-pagina en de
  // mobiele schil (KennisagentMobile), zodat ChatTurn maar één keer bestaat.
  const conversation = (
    <>
      <div className="space-y-6">
        {turns.map((turn, idx) => (
          <ChatTurn
            key={idx}
            turn={turn}
            priorMessages={turns.slice(0, idx).flatMap((t) => [
              { role: 'user' as const, content: t.question },
              { role: 'assistant' as const, content: t.answer.answer },
            ])}
            isLast={idx === turns.length - 1}
            onChangeScope={() => setScopePickerOpen(true)}
            chatId={currentChatId}
            canBackToSara={saraSoep !== null}
            onVerwijsbrief={user?.verwijsbrief ? maakBriefVoor : undefined}
            onBackToSara={(advies) => {
              if (!saraSoep) return;
              const cleanAdvies = stripDisclaimer(stripCitations(advies));
              // Mila's antwoord blijft naast het verslag staan, nooit in de P:
              // kennis in het dossier van één patiënt schuiven is precies de
              // MDR-grens (toets 12 okt 2026).
              navigate('/sara', {
                state: {
                  returnedSoep: saraSoep,
                  returnedKennis: cleanAdvies,
                  returnedSummary: saraSummary,
                  returnedTaskId: saraTaskId,
                },
              });
            }}
          />
        ))}
      </div>

      {pending && (
        <div className="mt-6 space-y-3">
          {pendingQuestion && (
            <div className="rounded-2xl bg-brand-50/70 px-5 py-3 text-sm text-ink-900">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-900/40">
                Je vraag
              </p>
              <p className="mt-1 whitespace-pre-wrap">{pendingQuestion}</p>
            </div>
          )}
          <div className="rounded-2xl border border-ring2 bg-white px-5 py-4 shadow-card">
            {streamingText ? (
              // Slice 7 — streaming render. Show the partial answer
              // as it arrives. We strip citation markers visually
              // (citations attach on the final response) so users
              // don't see raw [bron: N] tags while reading. The
              // legacy [chunkId: …] form is stripped too for
              // backward-compatibility with in-flight responses.
              <div className="space-y-2">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-900">
                  {renderBold(streamingText.replace(/\[(?:bron|chunkId):[^\]]*\]/gi, ''))}
                </p>
                <div className="flex items-center gap-2 text-[11px] text-ink-900/45">
                  <TypingDots />
                  <span>Antwoord is onderweg…</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <TypingDots />
                <span className="text-sm text-ink-900/70">Kennisagent denkt na…</span>
              </div>
            )}
            {showAbort && (
              <button
                type="button"
                onClick={cancel}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-900/60 hover:text-ink-900"
              >
                <XIcon size={13} /> Annuleren
              </button>
            )}
          </div>
        </div>
      )}

      {errorCopy && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
          <p>{errorCopy}</p>
        </div>
      )}

      {/* PII-trap 2 blijft een harde tussenstop: de vraag gaat pas door
                    als de gebruiker zelf bevestigt. De waarschuwing is daarom
                    onveranderd; alleen de twee acties waren onvindbaar (melding
                    behandelaar 2026-08 — twee kleine amberknopjes lazen als
                    uitleg). De bevestiging is nu een volwaardige primaire knop
                    met dezelfde bewoording als de waarschuwingszin. */}
      {naamBevestiging && (
        <div
          ref={naamBevestigingRef}
          className="mt-6 rounded-2xl border-2 border-amber-400 bg-amber-50 px-5 py-4 text-sm text-amber-900"
        >
          <div className="flex items-start gap-3">
            <AlertTriangleIcon size={20} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Mogelijke persoonsnaam in je vraag</p>
              <p className="mt-1">{naamBevestiging.message}</p>
            </div>
          </div>
          {/* De corrigerende actie blijft de primaire knop. De melding
                        ging over vindbaarheid, en die is opgelost met de zwaardere
                        rand, de kop, het in beeld scrollen en twee knoppen die als
                        knop lezen. Het omkeren van de nadruk zou de omzeiling van
                        een PII-poort tot opvallendste actie maken, en wie de vorm
                        van dit venster kent klikt de primaire knop reflexmatig,
                        ook bij een echte patiëntnaam. Er is geen tweede stop. */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setNaamBevestiging(null);
                draftRef.current?.focus();
              }}
              className="rounded-lg bg-amber-900 px-5 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-amber-800"
            >
              Vraag aanpassen
            </button>
            <button
              type="button"
              onClick={() => {
                const mode = naamBevestiging.mode;
                setNaamBevestiging(null);
                void submit(mode, { naamBevestigd: true });
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-900/40 bg-white px-4 py-2.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
            >
              <SendHorizontalIcon size={16} />
              Dit is geen persoonsnaam, vraag versturen
            </button>
          </div>
        </div>
      )}

      <div ref={bottomRef} />

      {/* Multi-turn context banner — surfaces ONLY when the thread has
                    grown past the 10-message sliding window (= 5 Q&A pairs).
                    Tells Linda the agent now keeps only the most recent 5 Q&As
                    verbatim plus a sticky head of the original question.
                    Hidden on shorter threads to avoid visual clutter. */}
      {!demo && turns.length >= 5 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Vanaf nu houdt de assistent alleen je laatste 5 vragen + antwoorden in context. Eerdere
          berichten staan in de geschiedenis maar tellen niet volledig mee voor vervolgvragen — de
          oorspronkelijke vraag blijft wel meelopen.
        </div>
      )}
    </>
  );

  // Mobiel (< md) krijgt de chat-schil; de demo-omgeving houdt overal de
  // bestaande pagina omdat die geen vrij invoerveld heeft (issue #41).
  // Eén afleiding voor beide schillen, zodat mobiel en desktop niet stil uit
  // elkaar lopen (de Sara-wisknop reset vijf state-slots).
  const stickyMode: AskMode | null = saraContext ? (saraContext.mode as AskMode) : null;
  const scopeActive = scopeTitles !== null && scopeTitles.length > 0;
  const scopeLabel = scopeActive
    ? `${scopeTitles!.length} publicatie${scopeTitles!.length === 1 ? '' : 's'}`
    : 'alle publicaties';
  const saraBanner = saraContext ? (
    <div className="flex items-center gap-1.5 rounded-lg border border-brand-100 bg-brand-50 px-3 py-2 text-[12px] text-ink-900/70">
      <MicIcon size={13} className="shrink-0 text-brand-600" />
      <span>
        {saraContext.mode === 'plan'
          ? 'Onderwerp uit Sara, zonder verslag'
          : 'Consult uit Sara (samenvatting + SOEP)'}{' '}
        —{' '}
        <span className="font-medium text-ink-900">
          {saraContext.mode === 'plan' ? 'Plan-opzet' : saraContext.mode}
        </span>
      </span>
      <button
        type="button"
        onClick={() => {
          setSaraContext(null);
          setSaraSoep(null);
          setSaraSummary('');
          setSaraTaskId(null);
          setDraft('');
        }}
        className="ml-auto text-ink-900/40 hover:text-ink-900"
        aria-label="Verwijder Sara-context"
      >
        <XIcon size={13} />
      </button>
    </div>
  ) : null;
  // Zolang de PII-naamtrap open staat mag de vraag niet stil vervangen
  // worden: de bevestiging geldt alleen voor de gecontroleerde tekst.
  const showStart = turns.length === 0 && !pending && !errorCopy && !naamBevestiging;

  if (isMobile && !demo) {
    return (
      <Layout>
        <div className="flex h-full">
          <ChatHistoryRail
            currentChatId={currentChatId}
            onPickChat={(id) => {
              void onPickChat(id);
              setHistoryOpen(false);
            }}
            onNewChat={() => {
              onNewChat();
              setHistoryOpen(false);
            }}
            refreshKey={historyRefreshKey}
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
          />
          <div className="min-w-0 flex-1">
            <KennisagentMobile
              hasTurns={!showStart}
              conversation={conversation}
              composerBanner={saraBanner}
              draft={draft}
              setDraft={setDraft}
              inputRef={draftRef}
              submit={(mode) => void submit(mode)}
              pending={pending}
              stickyMode={stickyMode}
              scopeLabel={scopeLabel}
              scopeActive={scopeActive}
              onOpenScope={() => setScopePickerOpen(true)}
              onClearScope={() => setScopeTitles(null)}
              onOpenHistory={() => setHistoryOpen(true)}
              onNewChat={onNewChat}
            />
          </div>
        </div>
        {scopePickerOpen && (
          <ScopePickerModal
            currentSelection={scopeTitles}
            onApply={(next) => {
              setScopeTitles(next);
              setScopePickerOpen(false);
            }}
            onClose={() => setScopePickerOpen(false)}
          />
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex h-full">
        {/* Ook in demo: de rail toont de drie geseede historische gesprekken
            (read-only); nieuwe demo-beurten worden niet opgeslagen. */}
        <ChatHistoryRail
          currentChatId={currentChatId}
          onPickChat={(id) => {
            void onPickChat(id);
            setHistoryOpen(false);
          }}
          onNewChat={() => {
            onNewChat();
            setHistoryOpen(false);
          }}
          refreshKey={historyRefreshKey}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
            <WelcomePanel variant="banner-only" />
            {demo ? (
              <header className="mb-6">
                {/* Onder md is de rail een uitschuiflade, dus stond "Nieuw
                  gesprek" daar buiten beeld (melding behandelaar 2026-08:
                  "je kunt geen nieuw gesprek beginnen"). Beide knoppen zijn
                  md:hidden, zodat ze op breed scherm niet dubbelen met de
                  altijd zichtbare rail. Ook in demo, want die heeft sinds #58
                  een read-only historie en onNewChat reset de demo-toestand. */}
                <div className="mb-3 flex flex-wrap items-center gap-2 md:hidden">
                  <button
                    type="button"
                    onClick={onNewChat}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-600"
                  >
                    <PlusIcon size={14} /> Nieuw gesprek
                  </button>
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 px-2.5 py-1.5 text-xs font-medium text-ink-800 hover:bg-brand-50"
                  >
                    <HistoryIcon size={14} /> Vorige gesprekken
                  </button>
                </div>
                <h1 className="text-2xl font-display font-semibold text-ink-900">Kennisagent</h1>
                <p className="mt-1 text-sm text-ink-900/60">
                  Stel een Nederlandstalige vraag over de {brand.kennisbank}. Antwoorden komen
                  uitsluitend uit geverifieerde bronnen.
                </p>
              </header>
            ) : (
              <div className="mb-6">
                <MilaGreeting />
              </div>
            )}

            {showStart &&
              (demo ? (
                <EmptyState demo />
              ) : (
                <div className="mb-8">
                  <ExampleQuestions
                    onPick={(q) => {
                      setDraft(q);
                      draftRef.current?.focus();
                    }}
                  />
                </div>
              ))}

            {conversation}

            {/* Demo: klikbare voorbeeldvragen i.p.v. het vrije invoerveld
                (issue #41). Vrij typen bestaat niet in de demo. */}
            {demo && (
              <div className="mt-6 space-y-3">
                {/* Doorvragen: gescripte vervolgvragen bij de laatst gekozen
                    hoofdvraag, of — als er een historisch gesprek uit de rail
                    open staat — de doorvragen die op dát gesprek aansluiten. */}
                {(() => {
                  const source = demoActiveQa
                    ? demoActiveQa.followUps
                    : ((currentChatId
                        ? demoChatQa.find((c) => c.chatId === currentChatId)?.followUps
                        : undefined) ?? []);
                  const remaining = source.filter((fu) => !demoAskedFollowUps.includes(fu.id));
                  if (remaining.length === 0) return null;
                  return (
                    <div className="space-y-2">
                      <p className="text-[13px] font-medium text-ink-900">
                        Vervolgvragen bij dit gesprek
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {remaining.map((fu) => (
                          <button
                            key={fu.id}
                            type="button"
                            onClick={() => askDemoFollowUp(fu)}
                            className="rounded-xl border border-brand-300 bg-brand-50 px-4 py-3 text-left text-[13px] leading-snug text-ink-900 shadow-card transition-colors hover:border-brand-700 hover:text-brand-800"
                          >
                            {fu.question}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <p className="text-[13px] font-medium text-ink-900">
                  {turns.length === 0
                    ? 'Kies een voorbeeldvraag. In de echte omgeving stel je elke vraag vrij.'
                    : 'Of kies een nieuwe voorbeeldvraag.'}
                </p>
                {demoQa === null ? (
                  <p className="text-sm text-ink-900/50">Voorbeeldvragen laden…</p>
                ) : demoQa.length === 0 ? (
                  <p className="text-sm text-ink-900/50">
                    De voorbeeldvragen zijn nog niet geladen. Ververs de pagina of neem contact op
                    met SUPPOHRT.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {demoQa.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => askDemo(item)}
                        className="rounded-xl border border-ring2 bg-white px-4 py-3 text-left text-[13px] leading-snug text-ink-900 shadow-card transition-colors hover:border-brand-700 hover:text-brand-800"
                      >
                        {item.question}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!demo && (
              <>
                <div className="mt-6">
                  <KennisagentComposer
                    draft={draft}
                    setDraft={setDraft}
                    inputRef={draftRef}
                    submit={(mode) => void submit(mode)}
                    pending={pending}
                    hasTurns={!showStart}
                    stickyMode={stickyMode}
                    banner={saraBanner}
                    scopeLabel={scopeLabel}
                    scopeActive={scopeActive}
                    onOpenScope={() => setScopePickerOpen(true)}
                    onClearScope={() => setScopeTitles(null)}
                  />
                </div>

                {scopePickerOpen && (
                  <ScopePickerModal
                    currentSelection={scopeTitles}
                    onApply={(next) => {
                      setScopeTitles(next);
                      setScopePickerOpen(false);
                    }}
                    onClose={() => setScopePickerOpen(false)}
                  />
                )}
              </>
            )}

            {demo && (
              <p className="mt-3 text-[11px] text-ink-900/40">
                De Kennisagent geeft geen diagnose of medicatieadvies. Verwijs daarvoor naar een
                professional.
              </p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Bewust géén kaart met rand en schaduw: een leeg wit kader vlak boven het
// echte invoerveld leest als een tweede invoerveld (melding behandelaar
// 2026-08 — "ik zie twee velden en weet niet waar ik moet typen").
function EmptyState({ demo = false }: { demo?: boolean }): JSX.Element {
  return (
    <p className="text-sm text-ink-900/55">
      {demo
        ? 'Kies hieronder een voorbeeldvraag. Het antwoord verschijnt met bronkaartjes erbij.'
        : 'Stel je eerste vraag in het veld onderaan. Antwoorden verschijnen met bronkaartjes erbij.'}
    </p>
  );
}

function ChatTurn({
  turn,
  priorMessages,
  isLast,
  onChangeScope,
  chatId,
  canBackToSara,
  onBackToSara,
  onVerwijsbrief,
}: {
  turn: ChatState;
  priorMessages: { role: 'user' | 'assistant'; content: string }[];
  isLast: boolean;
  onChangeScope: () => void;
  chatId: string | null;
  canBackToSara: boolean;
  onBackToSara: (advies: string) => void;
  /** Alleen aanwezig als de verwijsbrief-functie aan staat (ADR 0017). */
  onVerwijsbrief?: (p: AgentProviderCitation) => Promise<void>;
}): JSX.Element {
  const { question, answer, scopeTitles } = turn;
  // Demo (issue #41): feedback, vertalen en zoekbereik werken op de live
  // backend en blijven in de demo dus achterwege.
  const { user } = useAuth();
  const demo = user?.demo === true;
  const isEmptyRetrieval = answer.outcome === 'empty_retrieval';
  const isCrisis = answer.outcome === 'crisis';
  // Linda feedback 2026-05-20 — English-translation toggle. Display-time
  // only: when `englishOverlay` is set, render that instead of the Dutch
  // original. The chat-history Firestore record is never mutated; the
  // toggle resets to Dutch on the next page load.
  const [englishOverlay, setEnglishOverlay] = useState<string | null>(null);
  const displayedAnswerText = englishOverlay ?? answer.answer;
  const citationGroups = groupCitations(answer.citations);
  const scopeLabel =
    scopeTitles && scopeTitles.length > 0
      ? `${scopeTitles.length} publicatie${scopeTitles.length === 1 ? '' : 's'}`
      : 'alle publicaties';
  return (
    <div className="space-y-3">
      {/* Question bubble — slim, brand-tinted, distinctly the user's voice */}
      <div className="rounded-2xl bg-brand-50/80 px-5 py-3 text-sm text-ink-900">
        <p className="whitespace-pre-wrap">{question}</p>
      </div>

      {/* Single answer container holding prose + sources + footer.
          Crisis state replaces the neutral border with a red-amber
          alert treatment + drops the scope header (no retrieval ran). */}
      <div
        className={`rounded-2xl border bg-white shadow-card ${
          isCrisis ? 'border-red-300 ring-1 ring-red-200' : 'border-ring2'
        }`}
      >
        {!isCrisis && (
          <div className="flex items-center gap-1.5 border-b border-ring2/70 px-5 py-2 text-[11px] text-ink-900/55">
            <BookTextIcon size={11} />
            <span>Zoekbereik: {scopeLabel}</span>
            {scopeTitles && scopeTitles.length > 0 && scopeTitles.length <= 3 && (
              <span className="text-ink-900/40 truncate">({scopeTitles.join(', ')})</span>
            )}
          </div>
        )}
        {isCrisis && (
          <div className="flex items-center gap-1.5 border-b border-red-200 bg-red-50 px-5 py-2 text-[11px] font-medium text-red-800">
            <AlertTriangleIcon size={12} />
            <span>Crisiswoorden herkend: geen inhoudelijk antwoord</span>
          </div>
        )}

        <div className="px-5 py-5">
          {isCrisis ? (
            <CrisisAnswer />
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-900">
              {renderAnswerProse(displayedAnswerText, answer.citations)}
            </p>
          )}
          {englishOverlay && !isCrisis && (
            <p className="mt-2 text-[11px] text-ink-900/55">
              Engels vertaling — citatienummers verwijzen nog naar de Nederlandse bronnen hieronder.
            </p>
          )}
          {isEmptyRetrieval && (
            <p className="mt-3 text-xs text-ink-900/50">{EMPTY_RETRIEVAL_HINT}</p>
          )}
          {/* AI Act-transparantie (art. 50): bij elk gegenereerd antwoord vast
              zichtbaar. Niet bij de crisistekst, die is vast en niet door AI
              geschreven. */}
          {!isCrisis && (
            <p className="mt-3 text-[11px] font-medium text-ink-900/55">
              AI-antwoord, controleer de bron.
            </p>
          )}
        </div>

        {citationGroups.length > 0 && (
          <div className="border-t border-ring2/70 px-5 pt-3 pb-1">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-900/55">
              <BookTextIcon size={12} />
              {/* De kaartjes tellen documenten, de [n]-nummers in de tekst
                  tellen fragmenten; bij verschil noemt de kop ze allebei. */}
              Bronnen ({citationCountLabel(citationGroups.length, answer.citations.length)})
            </div>
            <ul className="divide-y divide-ring2/50">
              {citationGroups.map((g) => (
                <CitationRow key={g.key} group={g} />
              ))}
            </ul>
          </div>
        )}

        {answer.providerCitations && answer.providerCitations.length > 0 && (
          <div className="border-t border-ring2/70 px-5 pt-3 pb-1">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-900/55">
              <MapPinIcon size={12} />
              Zorgaanbieders ({answer.providerCitations.length})
            </div>
            <ul className="divide-y divide-ring2/50">
              {answer.providerCitations.map((p) => (
                <ProviderCitationRow key={p.providerId} provider={p} />
              ))}
            </ul>
            {onVerwijsbrief && (
              <VerwijsbriefVraag providers={answer.providerCitations} onKies={onVerwijsbrief} />
            )}
          </div>
        )}

        {!demo && (
          <FeedbackFooter
            answer={displayedAnswerText}
            chatId={chatId}
            messageId={answer.requestId}
            query={question}
            priorMessages={priorMessages}
            citations={answer.citations}
            translatable={!isCrisis}
            englishOverlay={englishOverlay}
            onTranslated={setEnglishOverlay}
            sourceText={answer.answer}
            canBackToSara={canBackToSara}
            onBackToSara={onBackToSara}
          />
        )}
      </div>

      {/* After-response scope hint on the most recent turn */}
      {!demo && isLast && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onChangeScope}
            className="text-[11px] text-ink-900/55 hover:text-brand-800 hover:underline"
          >
            Niet wat je zocht? Beperk het zoekbereik tot specifieke publicaties →
          </button>
        </div>
      )}
    </div>
  );
}

function FeedbackFooter({
  answer,
  chatId,
  messageId,
  query,
  priorMessages,
  citations,
  translatable = false,
  englishOverlay = null,
  onTranslated,
  sourceText,
  canBackToSara,
  onBackToSara,
}: {
  answer: string;
  chatId: string | null;
  messageId?: string;
  query: string;
  priorMessages: { role: 'user' | 'assistant'; content: string }[];
  citations: AgentCitation[];
  translatable?: boolean;
  englishOverlay?: string | null;
  onTranslated?: (next: string | null) => void;
  sourceText?: string;
  canBackToSara?: boolean;
  onBackToSara?: (advies: string) => void;
}): JSX.Element {
  const [translating, setTranslating] = useState(false);
  const [cachedTranslation, setCachedTranslation] = useState<string | null>(englishOverlay);
  const [translateError, setTranslateError] = useState<string | null>(null);

  async function handleTranslate(): Promise<void> {
    if (!onTranslated || !sourceText) return;
    // Toggle off: revert to Dutch
    if (englishOverlay) {
      onTranslated(null);
      return;
    }
    // Cached: reuse without a backend roundtrip
    if (cachedTranslation) {
      onTranslated(cachedTranslation);
      return;
    }
    if (!chatId || !messageId) return;
    setTranslating(true);
    setTranslateError(null);
    try {
      const resp = await apiPost<{ text: string }>('/api/v1/agent/translate', {
        chatId,
        messageId,
        sourceText,
      });
      setCachedTranslation(resp.text);
      onTranslated(resp.text);
    } catch (err) {
      setTranslateError(
        err instanceof ApiError ? 'Vertaling tijdelijk niet beschikbaar.' : 'Vertaling mislukt.',
      );
    } finally {
      setTranslating(false);
    }
  }

  return (
    <div className="border-t border-ring2/70">
      <div className="flex items-center gap-1 px-3 py-2">
        <CopyIconButton text={answer} />
        {canBackToSara && onBackToSara && (
          <button
            type="button"
            onClick={() => onBackToSara(sourceText ?? answer)}
            title="Ga terug naar Sara; dit antwoord staat daar naast het verslag"
            className="ml-1 inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50 hover:text-brand-800"
          >
            <ClipboardListIcon size={13} />
            Terug naar Sara
          </button>
        )}
        {translatable && (
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translating || (!sourceText && !englishOverlay)}
            title={
              englishOverlay
                ? 'Toon Nederlandse origineel'
                : (translateError ?? 'Vertaal antwoord naar Engels')
            }
            className={`ml-1 inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium transition-colors disabled:opacity-50 ${
              englishOverlay
                ? 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                : 'text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900'
            }`}
            aria-label={
              englishOverlay ? 'Toon Nederlandse origineel' : 'Vertaal antwoord naar Engels'
            }
          >
            {translating ? 'Vertalen…' : englishOverlay ? 'NL ↺' : 'EN'}
          </button>
        )}
        {translateError && !translating && (
          <span className="ml-1 text-[11px] text-red-700">{translateError}</span>
        )}
      </div>
      <AnswerRating
        context={{
          surface: 'kennisagent',
          chatId,
          messageId,
          query,
          priorMessages,
          answerExcerpt: answer,
          citations: citations.map((c) => ({
            chunkId: c.chunkId,
            filename: c.sourceFile,
            documentTitle: c.title,
          })),
        }}
      />
    </div>
  );
}

const RATING_REASONS = [
  'Klopt inhoudelijk niet',
  'Bron past niet',
  'Niet bruikbaar in de spreekkamer',
  'Te algemeen',
  'Mist iets',
];

/**
 * Beoordeling als eigen strook onder het antwoord (prototype-variant A, keuze
 * Jos 12 okt 2026). De kleine sterren met uitleg eronder werden over het hoofd
 * gezien; een vraag als kop plus inline redenen maakt het een uitnodiging.
 * Pas "Verstuur" maakt het change_request (ADR 0016); tot dan mag je de
 * sterren nog wijzigen. Redenen gaan als tekstregel mee, zodat schema en
 * beheerwachtrij ongewijzigd blijven.
 */
function AnswerRating({
  context,
}: {
  context: Extract<WijzigingsverzoekContext, { surface: 'kennisagent' }>;
}): JSX.Element {
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const shown = hover ?? rating ?? 0;
  const low = rating !== null && rating <= 3;

  async function submit(): Promise<void> {
    if (rating === null || submitting) return;
    setSubmitting(true);
    setError(null);
    const text = [low && reasons.length ? `Redenen: ${reasons.join(', ')}` : '', note.trim()]
      .filter(Boolean)
      .join('\n\n');
    try {
      await apiPost('/api/v1/change-requests', buildPayload({ ...context, rating }, text));
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Versturen mislukt (${err.status}). Probeer het opnieuw.`
          : 'Versturen mislukt. Probeer het opnieuw.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="flex items-center gap-2 rounded-b-2xl bg-brand-50 px-5 py-4 text-[13px] text-brand-800">
        <CheckIcon size={15} />
        Dank je. De beheerder van de Kennisbank bekijkt elke beoordeling en past de Kennisbank waar
        nodig aan.
      </div>
    );
  }

  return (
    <div className="rounded-b-2xl bg-brand-50 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[14px] font-semibold text-ink-900">Hoe bruikbaar was dit antwoord?</p>
          <p className="text-[12px] text-ink-900/60">
            Jouw oordeel is de manier waarop Mila beter wordt.
          </p>
        </div>
        {/* ponytail: vijf gewone knoppen, geen radiogroup; met Tab bereikbaar en
            elk met eigen label en aria-pressed. */}
        <div className="flex items-center gap-2 text-[11px] text-ink-900/50">
          <span aria-hidden="true">Niet</span>
          <div className="flex items-center gap-0.5" onMouseLeave={() => setHover(null)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={rating === n}
                aria-label={`${n} van 5 sterren`}
                title={`${n} van 5 sterren`}
                onMouseEnter={() => setHover(n)}
                onFocus={() => setHover(n)}
                onBlur={() => setHover(null)}
                onClick={() => setRating(n)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-mosterdgeel-500/10"
              >
                <StarIcon
                  size={22}
                  className={
                    n <= shown ? 'fill-mosterdgeel-500 text-mosterdgeel-500' : 'text-ink-100'
                  }
                />
              </button>
            ))}
          </div>
          <span aria-hidden="true">Heel</span>
        </div>
      </div>
      {rating !== null && (
        <div className="mt-3 space-y-3">
          {low && (
            <div className="flex flex-wrap gap-2">
              {RATING_REASONS.map((r) => {
                const on = reasons.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setReasons(on ? reasons.filter((x) => x !== r) : [...reasons, r])
                    }
                    className={`rounded-full border px-3 py-1 text-[12px] transition-colors ${
                      on
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-ring2 bg-white text-ink-900/70 hover:border-brand-600'
                    }`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            aria-label="Toelichting bij je beoordeling"
            placeholder={
              low ? 'Wat had je verwacht? (optioneel)' : 'Wat maakte het bruikbaar? (optioneel)'
            }
            className="w-full rounded-lg border border-ring2 bg-white px-3 py-2 text-[13px] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200/40"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="rounded-full bg-brand-600 px-4 py-1.5 text-[13px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Versturen…' : 'Verstuur beoordeling'}
            </button>
            <span className="text-[11px] text-ink-900/50">
              Geen spoed of klachten over zorgverlening: daarvoor de huisarts of
              klachtenfunctionaris.
            </span>
          </div>
          {error && <p className="text-[12px] text-red-700">{error}</p>}
        </div>
      )}
    </div>
  );
}

function CopyIconButton({ text }: { text: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Gekopieerd' : 'Antwoord kopiëren'}
      aria-label={copied ? 'Gekopieerd' : 'Antwoord kopiëren'}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-900/50 hover:bg-ink-900/5 hover:text-ink-900"
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </button>
  );
}

interface FullChunk {
  chunkId: string;
  sourceFile: string;
  page: number;
  chunkIndex: number;
  chunkText: string;
  tenantId: string;
  createdAt?: string;
  createdBy?: string;
  embeddingPresent?: boolean;
}

/**
 * Hand-crafted JSX for the crisis answer. Backend returns a plain-text
 * version of the same content (preserved for audit + non-web clients);
 * the web surface overrides it with this structured layout so phone
 * numbers tap-to-call on mobile and URLs are real anchors. Wording must
 * stay aligned with prompts.py CRISIS_RESPONSE.
 */
function CrisisAnswer(): JSX.Element {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-ink-900">
      <p>
        Je vraag bevat woorden die op een crisis of suïciderisico kunnen wijzen. Mila geeft daarom
        geen inhoudelijk antwoord. Volg je eigen crisisprotocol en overleg met de regiebehandelaar.
      </p>

      <div>
        <p className="font-medium text-ink-900">Bereikbaar voor de cliënt:</p>
        <ul className="mt-2 space-y-3 pl-1">
          <li>
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold">113 Zelfmoordpreventie:</span>
              <span className="text-ink-900/60">gratis, dag en nacht bereikbaar.</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-3 pl-3 text-[13px]">
              <a
                href="tel:08000113"
                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 font-medium text-red-700 hover:bg-red-50"
              >
                Bel 0800-0113
              </a>
              <a
                href="https://www.113.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 font-medium text-red-700 hover:bg-red-50"
              >
                Chat via 113.nl
              </a>
            </div>
          </li>
          <li>
            <div className="flex items-baseline gap-1.5">
              <span className="font-semibold">Bij spoed:</span>
              <span>
                bel{' '}
                <a href="tel:112" className="font-semibold text-red-700 hover:underline">
                  112
                </a>{' '}
                of de huisartsenpost.
              </span>
            </div>
          </li>
        </ul>
      </div>

      <p className="text-ink-900/80">
        Achtergrond over suïcidaal gedrag staat in de{' '}
        <a
          href="https://www.ggzstandaarden.nl/zorgstandaarden/diagnostiek-en-behandeling-van-suicidaal-gedrag"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-700 hover:underline"
        >
          Generieke module Diagnostiek en Behandeling van Suïcidaal Gedrag
        </a>{' '}
        op GGZ-standaarden.nl.
      </p>
    </div>
  );
}

/** Slice 8 — provider citation card. Shown beneath the chunk citations
 *  when the model called find_zorgaanbieders and cited a provider via
 *  [providerId: <id>]. Click-through goes to the Sociale Kaart page,
 *  pre-filtered by the provider's name. Compact compared to chunk
 *  citations — no snippet, just name + city + type chip + specs.
 */
const PROVIDER_TYPE_LABELS: Record<string, string> = {
  ggz_instelling: 'GGZ-instelling',
  vrijgevestigd: 'Vrijgevestigd',
  e_health: 'E-health',
  groepspraktijk: 'Groepspraktijk',
  overig: 'Overig',
};

function ProviderCitationRow({ provider }: { provider: AgentProviderCitation }): JSX.Element {
  // Deep-link via providerId — the SK page reads ?providerId=<id>, fetches
  // the single provider, and renders just that card with a "Toon alle
  // aanbieders" reset banner. Free-text q would only narrow the list,
  // not focus on the specific provider; ID lookup is unambiguous.
  const linkHref = `/sociale-kaart?providerId=${encodeURIComponent(provider.providerId)}`;
  const typeLabel = provider.type ? (PROVIDER_TYPE_LABELS[provider.type] ?? provider.type) : null;
  const locationLabel = [provider.city, provider.province].filter(Boolean).join(', ');
  const specs = provider.specialisations ?? [];
  return (
    <li className="py-2 first:pt-1 last:pb-1">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-700 text-white">
          <MapPinIcon size={11} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-1.5">
            <a
              href={linkHref}
              className="text-sm font-semibold text-ink-900 hover:text-brand-700 hover:underline"
              title="Bekijk in Sociale Kaart"
            >
              {provider.name}
            </a>
            {typeLabel && (
              <span className="rounded-full border border-brand-200 bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-ink-800">
                {typeLabel}
              </span>
            )}
          </div>
          {locationLabel && <p className="mt-0.5 text-xs text-ink-900/65">{locationLabel}</p>}
          {specs.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {specs.slice(0, 5).map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-mosterdgeel-50 px-1.5 py-0.5 text-[10px] font-medium text-mosterdgeel-600"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function CitationRow({ group }: { group: CitationGroup<AgentCitation> }): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'suppohrt_admin';
  const [expanded, setExpanded] = useState(false);
  // Admin-chunkinspectie is per fragment, dus houdt de state het geopende
  // chunkId vast in plaats van een enkele boolean.
  const [openChunkId, setOpenChunkId] = useState<string | null>(null);

  // Alle chunks in een groep delen ring, titel, auteur en bronbestand
  // (dat is de groepeersleutel), dus de eerste bepaalt de kaartmetadata.
  const citation = group.citations[0];
  // Pagina's samengevat op één kaartje: ontdubbeld en oplopend, zodat
  // "p. 49, 69, 71" leest als een vindlijst in het document.
  const pages = Array.from(new Set(group.citations.map((c) => c.page))).sort((a, b) => a - b);

  // De agent schrijft de sleutel `sourceFile` altijd en zet er een lege
  // string in als het manifest hem mist, dus `??` sloeg nooit aan: de link
  // werd `/api/v1/agent/sources/` en dat geeft de kale HTML-404 van Express.
  // De oude fallback reconstrueerde de bestandsnaam uit de weergavetitel plus
  // `.pdf`, wat een gok is zodra titel en bestandsnaam verschillen of de bron
  // geen PDF is. Zonder bruikbare bestandsnaam tonen we liever geen link dan
  // een dode link.
  const filename = citation.sourceFile?.trim() ?? '';
  // Ring 2/3 verwijzen naar het externe artikel. Bij die ringen staat in
  // `sourceFile` de gecrawlde URL in plaats van een bestandsnaam; die bevat
  // een schuine streep en zou op de bronnen-endpoint een 400 opleveren.
  const isExternalRing = citation.ring === 'ring2' || citation.ring === 'ring3';
  const externalUrl = isExternalRing && citation.sourceUrl ? citation.sourceUrl : null;
  // Een bestandsnaam is alleen bruikbaar voor de bronnen-endpoint als hij
  // gevuld is en geen pad bevat.
  const hasSourceFile = filename !== '' && !filename.includes('/');
  // Demo (issue #41): er is geen GCS-bestand achter de gescripte Ring
  // 1-citaties, dus daar blijft het bij een titel zonder link.
  const demo = user?.demo === true;
  const sourceUrl =
    externalUrl ??
    (!isExternalRing && !demo && hasSourceFile
      ? `/api/v1/agent/sources/${encodeURIComponent(filename)}`
      : null);
  // Alleen dit geval is een defect: een corpusdocument waarvan de bestandsnaam
  // ontbreekt, waardoor de link zou doodlopen. Een ontbrekende link op zichzelf
  // is dat niet. In de demo (issue #41) hoort er geen bestand achter te zitten
  // en een Ring 2/3-citatie zonder `sourceUrl` heeft simpelweg geen doel, dus
  // daar zou de waarschuwing liegen en blijft de tooltip weg.
  const sourceFileMissing = !isExternalRing && !demo && !hasSourceFile;
  const subtitle =
    citation.ring === 'ring2'
      ? citation.ring2Source && RING2_SOURCE_LABELS[citation.ring2Source]
        ? `Geverifieerde bron · ${RING2_SOURCE_LABELS[citation.ring2Source]}`
        : 'Geverifieerde externe bron'
      : citation.ring === 'ring3'
        ? 'Werkblad · Meesters in de Psychologie'
        : citation.author && citation.author !== 'Onbekend'
          ? citation.author
          : 'Kennisbank SUPPOHRT';
  const SourceIcon = pickSourceIcon(filename);

  return (
    <li className="py-2 first:pt-1 last:pb-1">
      <div className="flex items-start gap-2.5">
        {/* Citation number — primary identifier matching the [N] markers in
            prose. Een gegroepeerd kaartje draagt alle nummers die ernaar
            verwijzen, anders is [3] in de tekst niet meer terug te vinden. */}
        <span className="mt-0.5 inline-flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-brand-700 px-1.5 text-[11px] font-semibold text-white">
          {group.indices.join(', ')}
        </span>
        {/* Vertrouwenslaag-chip. Labels, tooltips en kleuren staan in
            RingChip.tsx, gedeeld met de legenda in het welkomstpaneel. */}
        <RingChip ring={citation.ring} ring2Source={citation.ring2Source} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 truncate text-sm font-medium text-ink-900 hover:text-brand-800 hover:underline"
                title={`Open ${filename}`}
              >
                <SourceIcon size={13} className="shrink-0 text-brand-700" />
                <span className="truncate">{citation.title}</span>
                <ExternalLinkIcon size={11} className="shrink-0 text-ink-900/40" />
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-1 truncate text-sm font-medium text-ink-900"
                title={
                  sourceFileMissing
                    ? 'Het bronbestand van deze citatie is niet beschikbaar om te openen.'
                    : undefined
                }
              >
                <SourceIcon size={13} className="shrink-0 text-brand-700" />
                <span className="truncate">{citation.title}</span>
              </span>
            )}
            <span className="shrink-0 text-[11px] text-ink-900/45">p. {pages.join(', ')}</span>
          </div>
          <div className="truncate text-[11px] text-ink-900/50">{subtitle}</div>

          {expanded && (
            <div className="mt-2 space-y-2">
              {group.citations.map((c) => (
                <div
                  key={c.chunkId}
                  className="rounded-lg bg-ink-900/[0.03] px-3 py-2 text-xs leading-relaxed text-ink-900/75"
                >
                  {group.citations.length > 1 && (
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-ink-900/45">
                      p. {c.page}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">{c.snippet}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-0.5 text-[11px] text-brand-700 hover:text-brand-800"
            >
              {expanded ? (
                <>
                  <ChevronUpIcon size={11} /> Verberg{' '}
                  {group.citations.length > 1 ? 'fragmenten' : 'fragment'}
                </>
              ) : (
                <>
                  <ChevronDownIcon size={11} /> Toon{' '}
                  {group.citations.length > 1 ? `${group.citations.length} fragmenten` : 'fragment'}
                </>
              )}
            </button>
            {isAdmin &&
              group.citations.map((c) => (
                <button
                  key={c.chunkId}
                  type="button"
                  onClick={() => setOpenChunkId(c.chunkId)}
                  className="font-mono text-[10px] text-ink-900/40 hover:text-brand-700 hover:underline"
                  title="Open volledige chunk-data (admin-validatie)"
                >
                  {c.chunkId.slice(0, 12)}…
                </button>
              ))}
          </div>
        </div>
      </div>

      {openChunkId && (
        <ChunkInspectorModal chunkId={openChunkId} onClose={() => setOpenChunkId(null)} />
      )}
    </li>
  );
}

function chunkToMarkdown(chunkId: string, data: FullChunk): string {
  const fence = '```';
  return [
    '## Chunk-validatie',
    '',
    `- **chunkId:** \`${chunkId}\``,
    `- **Bronbestand:** ${data.sourceFile}`,
    `- **Pagina:** ${data.page}`,
    `- **Chunk-index in document:** ${data.chunkIndex}`,
    `- **Tenant:** ${data.tenantId}`,
    `- **Geïndexeerd op:** ${data.createdAt ?? '—'}`,
    `- **Door:** ${data.createdBy ?? '—'}`,
    `- **Embedding aanwezig:** ${data.embeddingPresent ? 'ja (768-dim)' : 'nee'}`,
    '',
    '### Volledige tekst',
    '',
    fence,
    data.chunkText,
    fence,
    '',
  ].join('\n');
}

function ChunkInspectorModal({
  chunkId,
  onClose,
}: {
  chunkId: string;
  onClose: () => void;
}): JSX.Element {
  const [data, setData] = useState<FullChunk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet<FullChunk>(`/api/v1/agent/chunks/${chunkId}`)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err) => {
        if (alive) setError(err instanceof ApiError ? err.message : 'Kon chunk niet ophalen.');
      });
    return () => {
      alive = false;
    };
  }, [chunkId]);

  const handleCopy = async (): Promise<void> => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(chunkToMarkdown(chunkId, data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900">Chunk-validatie</h2>
            <p className="mt-1 font-mono text-[11px] text-ink-900/55 break-all">{chunkId}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!data}
              title={copied ? 'Gekopieerd als markdown' : 'Kopieer als markdown'}
              aria-label={copied ? 'Gekopieerd als markdown' : 'Kopieer als markdown'}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900 disabled:opacity-40"
            >
              {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900"
              aria-label="Sluit"
            >
              <XIcon size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        )}
        {!data && !error && <div className="mt-4 text-sm text-ink-900/55">Laden…</div>}
        {data && (
          <dl className="mt-4 space-y-3 text-sm">
            <Field label="Bronbestand">
              <a
                href={`/api/v1/agent/sources/${encodeURIComponent(data.sourceFile)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
              >
                {data.sourceFile} <ExternalLinkIcon size={12} />
              </a>
            </Field>
            <Field label="Pagina">{data.page}</Field>
            <Field label="Chunk-index in document">{data.chunkIndex}</Field>
            <Field label="Tenant">{data.tenantId}</Field>
            <Field label="Geïndexeerd op">{data.createdAt ?? '—'}</Field>
            <Field label="Door">{data.createdBy ?? '—'}</Field>
            <Field label="Embedding aanwezig">
              {data.embeddingPresent ? 'ja (768-dim)' : 'nee'}
            </Field>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-900/50">
                Volledige tekst
              </dt>
              <dd className="mt-1 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-xl bg-ink-900/5 p-3 text-xs leading-relaxed text-ink-900/85">
                {data.chunkText}
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-900/50">{label}</dt>
      <dd className="text-sm text-ink-900">{children}</dd>
    </div>
  );
}

function ScopePickerModal({
  currentSelection,
  onApply,
  onClose,
}: {
  currentSelection: string[] | null;
  onApply: (next: string[] | null) => void;
  onClose: () => void;
}): JSX.Element {
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // Local working set — applied on save, discarded on cancel.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(currentSelection ?? []));

  useEffect(() => {
    let alive = true;
    apiGet<DocumentsResponse>('/api/v1/agent/documents')
      .then((d) => {
        if (alive) setDocs(d.documents);
      })
      .catch((err) => {
        if (alive)
          setError(err instanceof ApiError ? err.message : 'Kon publicatielijst niet laden.');
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = (title: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const filtered = (docs ?? []).filter((d) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return d.documentTitle.toLowerCase().includes(q) || d.author.toLowerCase().includes(q);
  });

  const handleApply = (): void => {
    if (selected.size === 0) {
      onApply(null); // empty selection == "all docs"
    } else {
      onApply(Array.from(selected));
    }
  };

  const handleSelectAll = (): void => setSelected(new Set());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-ring2/70 px-6 pt-5 pb-3">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Zoekbereik</h2>
            <p className="mt-1 text-xs text-ink-900/55">
              Selecteer specifieke publicaties om je vraag tot die documenten te beperken. Geen
              selectie = alle publicaties (standaard).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-ink-900/55 hover:bg-ink-900/5 hover:text-ink-900"
            aria-label="Sluit"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="border-b border-ring2/70 px-6 py-3">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter op titel of auteur…"
            className="w-full rounded-lg border border-ring2 bg-white px-3 py-2 text-sm placeholder:text-ink-900/35 focus:border-brand-700 focus:outline-none"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {error && (
            <div className="rounded-xl border border-amber-300/70 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {error}
            </div>
          )}
          {!docs && !error && <div className="text-sm text-ink-900/55">Publicaties laden…</div>}
          {docs && filtered.length === 0 && (
            <div className="text-sm text-ink-900/55">
              Geen publicaties gevonden voor "{filter}".
            </div>
          )}
          {docs && filtered.length > 0 && (
            <ul className="divide-y divide-ring2/50">
              {filtered.map((d) => {
                const isSelected = selected.has(d.documentTitle);
                return (
                  <li key={d.sourceFile}>
                    <button
                      type="button"
                      onClick={() => toggle(d.documentTitle)}
                      className={`flex w-full items-start gap-3 py-2 text-left ${
                        isSelected ? 'text-ink-900' : 'text-ink-900/85 hover:text-ink-900'
                      }`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? 'border-brand-700 bg-brand-700 text-white'
                            : 'border-ring2 bg-white'
                        }`}
                      >
                        {isSelected && <CheckIcon size={11} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {d.documentTitle}
                        </span>
                        <span className="block truncate text-[11px] text-ink-900/55">
                          {d.author}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-ring2/70 px-6 py-3">
          <div className="text-[11px] text-ink-900/55">
            {selected.size === 0
              ? 'Geen selectie — alle publicaties (standaard)'
              : `${selected.size} publicatie${selected.size === 1 ? '' : 's'} geselecteerd`}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rounded-lg px-3 py-1.5 text-xs text-ink-900/65 hover:text-ink-900"
            >
              Alles wissen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-ring2 bg-white px-3 py-1.5 text-xs text-ink-900/70 hover:text-ink-900"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-lg bg-brand-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
            >
              Toepassen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Three bouncing dots — drop-in typing indicator. Uses Tailwind's built-in
 * `animate-bounce` with staggered `animation-delay`. The spans share a
 * single keyframe so the cadence is in lockstep across the dots.
 */
function TypingDots(): JSX.Element {
  return (
    <span aria-label="Kennisagent denkt na" className="inline-flex items-center gap-1">
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '0ms' }}
      />
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-700"
        style={{ animationDelay: '300ms' }}
      />
    </span>
  );
}

function VerwijsbriefVraag({
  providers,
  onKies,
}: {
  providers: AgentProviderCitation[];
  onKies: (p: AgentProviderCitation) => Promise<void>;
}): JSX.Element {
  const [bezig, setBezig] = useState<string | null>(null);
  const [fout, setFout] = useState(false);
  return (
    <div className="my-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5">
      <p className="mb-2 text-[13px] text-ink-900">
        Wil je dit omzetten in een verwijsbrief? Kies de praktijk:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {providers.map((p) => (
          <button
            key={p.providerId}
            type="button"
            disabled={bezig !== null}
            onClick={() => {
              setBezig(p.providerId);
              setFout(false);
              onKies(p).catch(() => {
                setBezig(null);
                setFout(true);
              });
            }}
            className="rounded-full border border-brand-300 bg-white px-3 py-1 text-[12px] font-medium text-brand-800 hover:border-brand-500 disabled:opacity-50"
          >
            {bezig === p.providerId ? 'Bezig…' : p.name}
          </button>
        ))}
      </div>
      {fout && (
        <p className="mt-1.5 text-[12px] text-error">Aanmaken mislukt, probeer het opnieuw.</p>
      )}
    </div>
  );
}
